import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { AppShell } from "../components/AppShell";
import { PrototypeControls, } from "../components/PrototypeControls";
import { PrototypeKeyMoves } from "../components/PrototypeKeyMoves";
import { StoryFeedGameScreenPhone } from "../components/StoryFeedGameScreenPhone";
import { nbaFeedLabFixture } from "../data/generated/nba-feed-lab";
import { anchorWatchActionLabelByGameId } from "../data/navigation-destinations";
import { gameScreenStatsByGameId } from "../data/nba-game-screen-stats";
import { fixtureArticles } from "../domain/articles";
import { consumerUnit } from "../domain/consumer-unit";
import { defaultDemoViewerState, useDemoViewer, viewingAccessForGame, } from "../domain/demo-viewer";
const articles = fixtureArticles(nbaFeedLabFixture);
const defaultPrototypeGameId = "0022501143";
const prototypeWalkthroughUrl = new URL("../assets/nba-pulse-prototype-walkthrough.mp4", import.meta.url).toString();
const prototypeWalkthroughPosterUrl = new URL("../assets/nba-pulse-prototype-walkthrough-poster.jpg", import.meta.url).toString();
export const prototypeGameOptions = [
    {
        id: "0022501143",
        label: "NYK @ ATL",
        description: "National · Peacock",
        defaultViewer: defaultDemoViewerState,
    },
    {
        id: "0022501147",
        label: "POR @ DEN",
        description: "League Pass",
        defaultViewer: {
            hasLeaguePass: true,
            isLoggedIn: true,
            showBettingOdds: true,
        },
    },
];
export const prototypeCuratedItemOrder = Object.fromEntries(prototypeGameOptions.map((option) => [
    option.id,
    {
        anchor: prototypeItemsForGame(option.id, "anchor").map((item) => item.display.id),
        end: prototypeItemsForGame(option.id, "end").map((item) => item.display.id),
    },
]));
function initialCheckpoint() {
    if (typeof window === "undefined") {
        return "anchor";
    }
    return new URLSearchParams(window.location.search).get("state") === "end"
        ? "end"
        : "anchor";
}
function storyForGame(gameId, stories) {
    const game = nbaFeedLabFixture.games.find((candidate) => candidate.id === gameId);
    if (!game) {
        return undefined;
    }
    return stories.find((story) => (story.destination?.type === "story" ||
        story.destination?.type === "story-snapshot") &&
        story.thumbnail?.primary === game.away.code &&
        story.thumbnail?.secondary === game.home.code);
}
export function prototypeItemsForGame(gameId, checkpoint) {
    const companion = nbaFeedLabFixture.companions.find((candidate) => candidate.gameId === gameId);
    if (!companion) {
        throw new Error(`Missing Prototype game account for ${gameId}`);
    }
    return companion[checkpoint].map(consumerUnit);
}
export function visibleGameFeedItems(items, showBettingOdds = true) {
    return showBettingOdds
        ? items
        : items.filter((item) => item.display.unitType !== "game-odds");
}
function PrototypeViewToggle({ onChange, view, }) {
    return (_jsx("div", { className: "prototype-view-toggle", children: _jsxs("div", { "aria-label": "Prototype view", className: "prototype-view-toggle__control", role: "group", children: [_jsx("button", { "aria-pressed": view === "use", onClick: () => onChange("use"), type: "button", children: "Use It" }), _jsx("button", { "aria-pressed": view === "see", onClick: () => onChange("see"), type: "button", children: "See It" })] }) }));
}
function PrototypeWalkthrough({ active }) {
    const videoRef = useRef(null);
    const [hasEnded, setHasEnded] = useState(false);
    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        if (!active) {
            video.pause();
            return;
        }
        if (!video.ended) {
            void video.play().catch(() => { });
        }
    }, [active]);
    const replay = useCallback(() => {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        video.currentTime = 0;
        setHasEnded(false);
        void video.play().catch(() => { });
    }, []);
    return (_jsxs("figure", { "aria-hidden": active ? undefined : true, className: "prototype-walkthrough", hidden: !active, inert: !active, children: [_jsxs("div", { className: "prototype-walkthrough__media", children: [_jsx("video", { "aria-label": "NBA Pulse in action", autoPlay: active, controls: true, muted: true, onEnded: () => setHasEnded(true), onPlay: () => setHasEnded(false), playsInline: true, poster: prototypeWalkthroughPosterUrl, preload: "metadata", ref: videoRef, src: prototypeWalkthroughUrl }), hasEnded ? (_jsx("div", { className: "prototype-walkthrough__replay", children: _jsx("button", { onClick: replay, type: "button", children: "Replay" }) })) : null] }), _jsx("figcaption", { children: _jsx("strong", { children: "NBA Pulse in action" }) })] }));
}
export function HomeFeedPage() {
    const { setViewer, viewer } = useDemoViewer();
    const [prototypeView, setPrototypeView] = useState("use");
    const [checkpoint, setCheckpoint] = useState(initialCheckpoint);
    const [resetRevision, setResetRevision] = useState(0);
    const [selectedGameId, setSelectedGameId] = useState(defaultPrototypeGameId);
    const stories = nbaFeedLabFixture.home.storiesTray[checkpoint];
    const companions = useMemo(() => prototypeGameOptions.map((option) => {
        const source = nbaFeedLabFixture.companions.find((candidate) => candidate.gameId === option.id);
        const game = nbaFeedLabFixture.games.find((candidate) => candidate.id === option.id);
        if (!source || !game) {
            throw new Error(`Missing Prototype game fixture for ${option.id}`);
        }
        return {
            gameId: option.id,
            title: game.short,
            matchup: source.matchup,
            score: checkpoint === "anchor"
                ? source.anchorState
                : source.finalState,
            game: {
                gameId: game.id,
                away: game.away,
                home: game.home,
                broadcasters: game.broadcasters,
                nationalBroadcasters: game.nationalBroadcasters,
                location: game.location,
                storyTitle: game.storyTitle,
                tipoff: game.tipoff,
                venue: game.venue,
            },
            items: visibleGameFeedItems(prototypeItemsForGame(option.id, checkpoint), viewer.showBettingOdds !== false),
            story: storyForGame(option.id, stories),
        };
    }), [checkpoint, stories, viewer.showBettingOdds]);
    const selectedGameCompanion = companions.find((companion) => companion.gameId === selectedGameId);
    if (!selectedGameCompanion) {
        throw new Error(`Missing selected Prototype game ${selectedGameId}`);
    }
    const viewingAccess = viewingAccessForGame(viewer, (selectedGameCompanion.game.nationalBroadcasters?.length ?? 0) > 0);
    const selectGame = useCallback((gameId) => {
        const option = prototypeGameOptions.find((candidate) => candidate.id === gameId);
        if (!option) {
            return;
        }
        setSelectedGameId(gameId);
        setViewer({
            ...option.defaultViewer,
            showBettingOdds: viewer.showBettingOdds !== false,
        });
    }, [setViewer, viewer.showBettingOdds]);
    const resetPrototype = useCallback(() => {
        setSelectedGameId(defaultPrototypeGameId);
        setCheckpoint("anchor");
        setResetRevision((current) => current + 1);
        if (typeof window !== "undefined") {
            window.history.replaceState(null, "", `/home/${window.location.hash}`);
            window.scrollTo({ top: 0 });
        }
    }, []);
    return (_jsx(AppShell, { active: "home", children: _jsxs("section", { className: "state-review state-review--prototype", children: [_jsx("h1", { className: "visually-hidden", children: "Game Screen Prototype" }), _jsx(PrototypeViewToggle, { onChange: setPrototypeView, view: prototypeView }), _jsxs("div", { className: `state-review__canvas state-review__canvas--${prototypeView}`, children: [_jsxs("div", { "aria-hidden": prototypeView === "see"
                                ? true
                                : undefined, className: "prototype-interactive", hidden: prototypeView === "see", inert: prototypeView === "see", children: [_jsx(StoryFeedGameScreenPhone, { articles: articles, broadcastActionLabel: anchorWatchActionLabelByGameId[selectedGameCompanion.gameId], companion: selectedGameCompanion, deviceTime: checkpoint === "anchor"
                                        ? "9:35"
                                        : "1:30", liveArrivalItemIds: checkpoint === "anchor" &&
                                        selectedGameCompanion.gameId ===
                                            "0022501143"
                                        ? [
                                            "nyk-atl-jesser-final-possession",
                                            "nyk-atl-live-anchor",
                                        ]
                                        : undefined, stats: gameScreenStatsByGameId[selectedGameCompanion.gameId]?.[checkpoint], viewingAccess: viewingAccess }, `${selectedGameCompanion.gameId}:${checkpoint}:${resetRevision}`), _jsx(PrototypeControls, { checkpoint: checkpoint, gameId: selectedGameId, games: prototypeGameOptions, onCheckpointChange: setCheckpoint, onGameChange: selectGame, onReset: resetPrototype }), _jsx(PrototypeKeyMoves, {})] }), _jsx(PrototypeWalkthrough, { active: prototypeView === "see" })] })] }) }));
}
